
import * as React from 'react';
import {
  ISimpleComponentDecorator,
  ISimpleDecoratedProps,
  withDisplayName,
  IComponentDecorator,
  IDecoratedProps
} from '../../utilities/components/Decorators';
import { IItemCache } from '../../models/IItemCacheObserver';
import { connectToItemCache } from './connectToItemCache';
import { isPageLoaded } from '../../operations/pageLoad';

/**
 * Props added by the afterPageLoad and withPageLoad decorators.
 * @beta
 */
export interface IAfterPageLoadProps {
  /**
   * If set, permits the component to render anyway.
   * This may be necessary if the component is typically defer-loaded,
   * but is being rendered as part of page load in a given scenario.
   */
  bypassPageLoadBarrier?: boolean;
}

/**
 * Props injects by the withPageLoad decorator.
 * @beta
 */
export interface IWithPageLoadProps {
  hasPageLoaded: boolean;
}

/**
 * Decorator which provides the state of the page load barrier into a wrapped component.
 * It is up to the wrapped component to decide how to react to the page load barrier.
 * @beta
 */
export function withPageLoad(): IComponentDecorator<IWithPageLoadProps, IAfterPageLoadProps> {
  return <P extends IWithPageLoadProps>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, IWithPageLoadProps, IAfterPageLoadProps>> => {
    return withDisplayName('WithPageLoad')(
      connectToItemCache((itemCache: IItemCache, props: IAfterPageLoadProps): IWithPageLoadProps => {
        return {
          hasPageLoaded: !!props.bypassPageLoadBarrier || isPageLoaded(itemCache)
        };
      })(component)
    );
  };
}

/**
 * Decorator which defers rendering of a wrapped component until after page load.
 * @beta
 */
export function afterPageLoad(): ISimpleComponentDecorator<IAfterPageLoadProps> {
  return <P extends {}>(
    component: React.ComponentType<P>
  ): React.ComponentType<ISimpleDecoratedProps<P, IAfterPageLoadProps>> => {
    const Component = component;

    const Conditional: React.ComponentType<P & IWithPageLoadProps> = withDisplayName('AfterPageLoad.Callback')((
      props: ISimpleDecoratedProps<P, IWithPageLoadProps>
    ): JSX.Element | null => {
      const {
        hasPageLoaded,
        ...componentProps
      } = props as IWithPageLoadProps;

      if (props.hasPageLoaded) {
        return (
          <Component { ...componentProps } />
        );
      }

      return null;
    });

    const AfterPageLoad = withPageLoad()(Conditional);

    // @ts-ignore
    return withDisplayName('AfterPageLoad')(
      AfterPageLoad
    );
  };
}
